import api from './axios';

const BASE = '/approvals';

const normalizePatient = (patient) => ({
  ...patient,
  id: patient?.id || patient?.patient_id,
});

const normalizePatientListResponse = (res) => {
  const data = res?.data || {};
  const normalizeList = (items) => (Array.isArray(items) ? items.map(normalizePatient) : items);

  if (Array.isArray(data)) {
    return {
      ...res,
      data: normalizeList(data),
    };
  }

  return {
    ...res,
    data: {
      ...data,
      data: normalizeList(data.data),
      results: normalizeList(data.results),
    },
  };
};

// ---------------------------------------------------------------------------
// Approval CRUD
// ---------------------------------------------------------------------------

/**
 * List approvals owned by the current biller, optionally filtered by status.
 * @param {Object} [params]
 * @param {string} [params.status] one of draft|sent|viewed|signed|declined|expired
 */
export const listApprovals = (params = {}) => {
  const config = {};
  if (params && params.status && params.status !== 'all') {
    config.params = { status: params.status };
  }
  return api.get(`${BASE}/`, config);
};

export const getApproval = (approvalId) =>
  api.get(`${BASE}/${approvalId}/`);

export const createApproval = (payload) =>
  api.post(`${BASE}/`, payload);

export const updateApproval = (approvalId, payload) =>
  api.patch(`${BASE}/${approvalId}/`, payload);

/**
 * PATCH an approval. Identical to `updateApproval` but named per the
 * evaluator spec (Sprint 2 feature 12: edit-draft path). The backend only
 * accepts PATCH when the approval is still in `draft` status.
 */
export const patchApproval = (approvalId, payload) =>
  api.patch(`${BASE}/${approvalId}/`, payload);

/**
 * Send a draft approval for the first time. POSTs with no body — token
 * rotation is driven server-side. Returns 200 with `{ status: "sent", id }`.
 */
export const sendApproval = (approvalId) =>
  api.post(`${BASE}/${approvalId}/send/`);

/**
 * Resend an already-sent or viewed approval. Rotates the secure link
 * server-side. Returns 200 with `{ status: "sent", id }`.
 */
export const resendApproval = (approvalId) =>
  api.post(`${BASE}/${approvalId}/resend/`);

// ---------------------------------------------------------------------------
// Supporting lookups (patients + documents)
// ---------------------------------------------------------------------------

/**
 * List patients. Forwards an optional `search` term to the backend so the
 * patient picker can do server-side filtering rather than loading the entire
 * roster client-side (PHI exposure + perf risk on large providers).
 */
export const listPatients = ({ search } = {}) => {
  const config = {};
  if (search && search.trim()) {
    config.params = { search: search.trim() };
  }
  return api.get('/patients/', config).then(normalizePatientListResponse);
};

export const listPatientDocuments = (patientId) =>
  api.get('/documents/uploads/', { params: { patient_id: patientId } }).then((res) => {
    const data = res?.data || {};
    return {
      ...res,
      data: {
        ...data,
        results: data.results || data.documents || [],
      },
    };
  });

export const uploadApprovalDocument = ({
  patientId,
  file,
  documentType = 'supporting_docs',
}) => {
  if (!patientId) {
    return Promise.reject(new Error('patientId is required to upload an approval document'));
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('patient', String(patientId));
  formData.append('document_type', documentType);

  return api.post('/documents/uploads/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }).then((res) => {
    const data = res?.data || {};
    return {
      ...res,
      data: {
        ...data,
        id: data.id || data.document_id,
        filename: data.filename || data.original_filename || file?.name || 'document',
        original_filename: data.original_filename || file?.name || '',
        document_type: data.document_type || documentType,
      },
    };
  });
};

/**
 * List billing providers configured on the current tenant. Used by the
 * "signing provider" step in the New Approval wizard.
 */
export const listBillingProviders = (params = {}) =>
  api.get('/claims/billing-providers/', { params });
